import React from "react";

import { Button, ButtonBsStyle } from "@common/components/Button";
import { Loading } from "@common/components/loading";
import { Flex } from "@common/components/Flex";
import { Header } from "@common/components/Header";
import { getTestAttributes } from "@common/utils/tests";
import { trDts } from "@modules/datasheets/utils/i18n";
import { useMessage } from "@common/hooks/message";
import { exportDatasheet, loadDatasheetFormat } from "@modules/datasheets/services/datasheet";
import { checkImportFile, importDatasheetFormat } from "@modules/datasheets/services/import";
import { executeUI } from "@common/utils/ui-bridge";

import { DatasheetFormatManagement } from "../datasheet-format-management";
import {
  DatasheetFormatManagementMode,
  DatasheetFormatManagementSubmitExportParams,
  DatasheetFormatManagementSubmitImportParams,
  DatasheetFormatManagementSubmitLoadParams,
  DatasheetFormatManagementSubmitParams,
} from "../datasheet-format-management/datasheet-format-management.types";
import { ImportDatasheetFieldsPicker } from "./components/import-datasheet-fields-picker";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { MessageBox } from "@common/components/MessageBox";

export type HomeProps = {};

export const Home: React.FC<HomeProps> = () => {
  const [datasheetFormatManagementMode, setDatasheetFormatManagementMode] = React.useState<
    DatasheetFormatManagementMode | undefined
  >();
  const [importFile, setImportFile] = React.useState<File | null>(null);
  const [importFolder, setImportFolder] = React.useState<FolderPrimaryListDto | null>(null);
  const [importContext, setImportContext] = React.useState<number | null>(null);
  const [importFormatName, setImportFormatName] = React.useState<string | null>(null);
  const [importTableFieldId, setImportTableFieldId] = React.useState<number | null>(null);
  const [importFoldersData, setImportFoldersData] = React.useState<{
    pedigreeFolders: any[];
    individualFolders: any[];
    pedigreeFields: Record<string, any[]>;
    individualFields: Record<string, any[]>;
    systemFields: Record<string, any[]>;
  } | null>(null);
  const [isDatabaseLocked, setIsDatabaseLocked] = React.useState<boolean>(false);
  const [isExporting, setIsExporting] = React.useState<boolean>(false);
  const [isCheckingImportFile, setIsCheckingImportFile] = React.useState<boolean>(false);
  const [isImporting, setIsImporting] = React.useState<boolean>(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { showErrorMessage, showInfoMessage ,closeMessage,messageProps} = useMessage();


  // Listen to Progeny lock events (dispatched from GWT Progeny.setDatabaseLocked)
  React.useEffect(() => {
    const handleLockEvent = (e: Event) => {
      const locked = (e as CustomEvent)?.detail?.locked;
      if (typeof locked === "boolean") {
        console.debug("[ImportLock] event progeny:databaseLock ->", locked);
        setIsDatabaseLocked(Boolean(locked));
      }
    };
    window.addEventListener("progeny:databaseLock", handleLockEvent as EventListener);
    // Initial sync from global flag if Progeny isn't available
    const initial = (window as any)?.ProgenyLockState;
    if (typeof initial === "boolean") {
      console.debug("[ImportLock] initial from ProgenyLockState ->", initial);
      setIsDatabaseLocked(initial);
    }
    return () => {
      window.removeEventListener("progeny:databaseLock", handleLockEvent as EventListener);
    };
  }, []);


  const closeDatasheetFormatManagementMode = React.useCallback(() => {
    setDatasheetFormatManagementMode(undefined);
  }, []);


  const handleImportFormatClick = React.useCallback(() => {
    // Always refresh lock state immediately before acting
    if (!isDatabaseLocked) {
      showInfoMessage("Lock database (profile dropdown) to enable Import Format.");
      return;
    }
    // Trigger file input click to open system file picker (same as GWT: fileUpload.click())
    fileInputRef.current?.click();
  }, [isDatabaseLocked, showInfoMessage]);

  const handleFileChange = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      // Reset the input value to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Handle user "Cancel" operation
      if (!file) {
        return;
      }

      // Validate file extension
      const fileExtension = file.name.substring(file.name.lastIndexOf(".") + 1).toLowerCase();
      if (fileExtension !== "xml") {
        showErrorMessage("Failed to import due to invalid datasheet format");
        return;
      }

      try {
        setIsCheckingImportFile(true);
        const result = await checkImportFile(file);
        setImportContext(result.tableFieldId!=0 ? 8 : result.context);
        setImportFormatName(result.formatName);
        setImportTableFieldId(result.tableFieldId || null);
        setImportFile(file);
        setImportFoldersData({
          pedigreeFolders: result.pedigreeFolders || [],
          individualFolders: result.individualFolders || [],
          pedigreeFields: result.pedigreeFields || {},
          individualFields: result.individualFields || {},
          systemFields: result.systemFields || {},
        });
        // Open folder picker in import mode
        setDatasheetFormatManagementMode(DatasheetFormatManagementMode.import);
      } catch (err) {
        console.error("Failed to check import file", err);
        showErrorMessage(trDts("Failed to get data"));
      } finally {
        setIsCheckingImportFile(false);
      }
    },
    [showErrorMessage]
  );

  const handleImportFieldsPickerSubmit = React.useCallback(
    async (params: {
      folder: FolderPrimaryListDto;
      file: File;
      createNewFields: boolean;
      individualFieldIds: number[];
      pedigreeFieldIds: number[];
    }) => {
      setIsImporting(true);
      try {
        await importDatasheetFormat(params.file, {
          folderId: params.folder.id,
          indFieldIds: params.individualFieldIds,
          pedFieldIds: params.pedigreeFieldIds,
          createFields: params.createNewFields,
        });
        showInfoMessage("Format has been successfully imported");
      } catch (err) {
        console.error("Failed to import datasheet format", err);
        const message =
          (err as Error | undefined)?.message ||
          "Failed to import format due to server error";
        showErrorMessage(message);
      } finally {
        setIsImporting(false);
        setImportFolder(null);
        setImportFile(null);
        setImportFoldersData(null);
      }
    },
    [showErrorMessage, showInfoMessage]
  );
  const handleDatasheetFormatManagementSubmit = React.useCallback(
    async (params: DatasheetFormatManagementSubmitParams) => {
      if (datasheetFormatManagementMode === DatasheetFormatManagementMode.export) {
        const exportParams = params as DatasheetFormatManagementSubmitExportParams;
        setIsExporting(true);
        try {
          await exportDatasheet({
            formatId: Number(exportParams.format.id),
            context: exportParams.context,
            tds: exportParams.tds,
          });
        } catch (err) {
          const message =
            (err as Error)?.message || trDts("Failed to export datasheet format");
          showErrorMessage(message);
        } finally {
          setIsExporting(false);
          closeDatasheetFormatManagementMode();
        }
        return;
      }
      if (datasheetFormatManagementMode === DatasheetFormatManagementMode.load) {
        const loadParams = params as DatasheetFormatManagementSubmitLoadParams;
        if (!loadParams?.format?.id || !loadParams?.context) {
          console.error("Missing format or context; cannot load datasheet.");
        } else {
          // For table datasheets, the backend loadDSFormat API expects context = 8 (TDS),
          // while the UI/context used elsewhere remains 1/2 (IDS/PDS).
          const isTableDatasheet = loadParams.tableContext != null;
          const apiContext = isTableDatasheet ? 8 : loadParams.context;

          try {
            // Close immediately to avoid flicker while loading the format.
            closeDatasheetFormatManagementMode();

            await loadDatasheetFormat({
              formatId: Number(loadParams.format.id),
              context: apiContext,
              tableContext: loadParams.tableContext,
            });

            // Open the editor (GWT parity behavior)
            const MODULE_NAME = "DATASHEETS";
            const ACTION = "EDIT";
            const tableFieldId = loadParams.tableFieldId ?? 0; // no table field in load flow; editor expects a value
            executeUI(
              MODULE_NAME,
              ACTION,
              loadParams.format.name || "Datasheet",
              loadParams.context,
              Number(loadParams.format.id),
              tableFieldId,
              loadParams.tableContext
            );
          } catch (err) {
            console.error("Failed to load datasheet format from Home Load dialog", err);
          }
        }
        return;
      }
      else
      if (datasheetFormatManagementMode === DatasheetFormatManagementMode.import && importFile) {
        const importParams = params as DatasheetFormatManagementSubmitImportParams;
        // Store folder and open field picker
        if (importParams?.folder) {
          // Immediately close the folder picker modal
          closeDatasheetFormatManagementMode();
          // Then set the folder to trigger the field picker modal
          setImportFolder(importParams.folder);
          // Open field picker (state-driven; importFolder triggers it)
          return;
        }
      }

      closeDatasheetFormatManagementMode();
    },
    [datasheetFormatManagementMode, closeDatasheetFormatManagementMode, importFile]
  );

  const headerActions: Array<{
    id: string;
    label: string;
    onClick: () => void;
    btnType?: ButtonBsStyle;
    disabled?: boolean;
  }> = [
    {
      id: getTestAttributes({ id: "data-sheet-formats-wnd-btn-Export Format" }).id,
      label: trDts("Export Format"),
      onClick: () => setDatasheetFormatManagementMode(DatasheetFormatManagementMode.export),
    },
    {
      id: getTestAttributes({ id: "data-sheet-formats-wnd-btn-Import Datasheet" }).id,
      label: trDts("Import Format"),
      onClick: handleImportFormatClick,
      disabled: !isDatabaseLocked,
    },
    {
      id: getTestAttributes({ id: "data-sheet-formats-wnd-btn-Load Datasheet" }).id,
      label: trDts("Load Datasheet"),
      btnType: "primary",
      onClick: () => setDatasheetFormatManagementMode(DatasheetFormatManagementMode.load),
    },
    {
      id: getTestAttributes({ id: "data-sheet-formats-wnd-btn-New Datasheet" }).id,
      label: trDts("New Datasheet"),
      btnType: "primary",
      onClick: () => setDatasheetFormatManagementMode(DatasheetFormatManagementMode.new),
    },
  ];

  return (
    <div>
      <MessageBox hideMessageBox={closeMessage} {...messageProps} />
      {(isExporting || isCheckingImportFile) && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.6)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loading size={60} />
        </div>
      )}
      <Header moduleName={trDts("Datasheets")}>
        <Flex width="100%" justify="flex-end">
          {headerActions.map((x) => (
            <Button
              style={{ marginLeft: "10px" }}
              key={x.id}
              text={x.label}
              bsStyle={x.btnType}
              id={x.id}
              data-id={x.id}
              disabled={x.disabled}
              onClick={x.onClick}
            />
          ))}
        </Flex>
      </Header>
      {datasheetFormatManagementMode && (
        <DatasheetFormatManagement
          onClose={closeDatasheetFormatManagementMode}
          getId={{
            title: getTestAttributes({ id: "load-fm-format-header" }).id,
            folderChooserBtn: getTestAttributes({ id: "fp-new-data-sheet-wnd-folder-picker" }).id,
            formatTypes: getTestAttributes({ id: "new-data-sheet-wnd-format-type-drop-btn" }).id,
            selectedFolderNameHolder: getTestAttributes({ id: "tx-new-data-sheet-wnd-folder" }).id,
            formatName: getTestAttributes({ id: "tx-new-data-sheet-wnd-ds-format-name" }).id,
          }}
          onSubmit={handleDatasheetFormatManagementSubmit}
          getFormats={() =>
            Promise.resolve([])
          }
          getFolders={() => Promise.resolve([])}
          mode={datasheetFormatManagementMode}
          checkFormatNameExists={() => Promise.resolve(false)}
          isExporting={isExporting}
          importContext={importContext ?? undefined}
          importFormatName={importFormatName ?? undefined}
          importTableFieldId={importTableFieldId ?? undefined}
        />
      )}
      {importFile && importFolder && importFoldersData && (
        <ImportDatasheetFieldsPicker
          show
          file={importFile}
          folder={importFolder}
          pedigreeFolders={importFoldersData.pedigreeFolders}
          individualFolders={importFoldersData.individualFolders}
          pedigreeFields={importFoldersData.pedigreeFields}
          individualFields={importFoldersData.individualFields}
          systemFields={importFoldersData.systemFields}
          isImporting={isImporting}
          onCancel={() => {
            setImportFolder(null);
            setImportFile(null);
            setImportFoldersData(null);
            setImportTableFieldId(null);
          }}
          onSubmit={handleImportFieldsPickerSubmit}
        />
      )}
      {/* Hidden file input for importing datasheet format (same as GWT: FormPanel with display: none) */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xml"
        name="Filedata"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </div>
  );
};
